import React from 'react'
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps'
import { StandardPin } from './pinStyle'

/**
 * Read-only sibling of LocationPickerModal. Shows a single location on a
 * Google Map with a pin, plus a footer of metadata + actions.
 *
 * Used in two contexts:
 *  - `LocationChips` (timeline) → tap a chip → viewer opens, no Edit button.
 *    Replaces the prior outbound-link-on-tap behavior; the viewer keeps
 *    "Open in Google Maps" as an explicit button.
 *  - `LocationListEditor` (composer) → tap an existing row → viewer opens
 *    with an Edit button that hands off to LocationPickerModal.
 *
 * The map is interactive (pan/zoom allowed for context) but the marker is
 * not draggable — this is read-only display, not capture.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
const VIEWER_MAP_ID = 'memgenie-location-viewer'
const VIEWER_DEFAULT_ZOOM = 15

export interface LocationViewerValue {
    lat: number
    lng: number
    name?: string | null
    address?: string | null
    googlePlaceId?: string | null
}

interface LocationViewerModalProps {
    open: boolean
    value: LocationViewerValue | null
    /** Optional: when set, an Edit button is shown that calls this. The
     *  viewer does not close itself on Edit — the caller decides whether to
     *  swap the viewer for the picker or close it first. */
    onEdit?: () => void
    onClose: () => void
}

const LocationViewerModal: React.FC<LocationViewerModalProps> = ({
    open,
    value,
    onEdit,
    onClose,
}) => {
    if (!open || !value) return null

    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <Backdrop onClose={onClose}>
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Location viewer unavailable</h3>
                    <p className="text-sm text-gray-600">
                        VITE_GOOGLE_MAPS_BROWSER_KEY is not set.
                    </p>
                </div>
            </Backdrop>
        )
    }

    const center = { lat: value.lat, lng: value.lng }
    const subtitle =
        value.address ||
        (value.name ? null : `${value.lat.toFixed(5)}°, ${value.lng.toFixed(5)}°`)

    return (
        <Backdrop onClose={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl flex flex-col w-[720px] max-w-[95vw] h-[560px] max-h-[90vh] overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="min-w-0 flex-1 pr-4">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                            {value.name || 'Pinned location'}
                        </h3>
                        {subtitle && (
                            <div className="text-xs text-gray-500 truncate">{subtitle}</div>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 relative">
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                        <Map
                            style={{ width: '100%', height: '100%' }}
                            defaultCenter={center}
                            defaultZoom={VIEWER_DEFAULT_ZOOM}
                            mapId={VIEWER_MAP_ID}
                            gestureHandling="greedy"
                            disableDefaultUI={false}
                        >
                            <AdvancedMarker position={center}>
                                <StandardPin />
                            </AdvancedMarker>
                        </Map>
                    </APIProvider>
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Edit
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm bg-action text-plum rounded hover:bg-action-dark"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Backdrop>
    )
}

const Backdrop: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
    onClose,
    children,
}) => (
    // mousedown-stop on backdrop matches LocationPickerModal — same reason
    // (parent ItemListEditorShell's document-level click-outside listener
    // would otherwise fire on every modal interaction).
    <div
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onClose}
    >
        {children}
    </div>
)

export default LocationViewerModal
